import { useArrowSource } from '@components/ArtBoard/Arrows/hooks/useArrowSource'
import type { ArrowUI } from '@components/ArtBoard/Arrows/types'
import type { AppState } from '@store/types'
import type React from 'react'
import { useSelector } from 'react-redux'

import { ArrowActionsToolbar } from '../ArrowActionsToolbar'

interface Props {
  arrow: ArrowUI
}

/**
 * One toolbar for the selected arrow, outside the arrows: `Arrows` remounts them on
 * every redraw, and a toolbar inside one, remounted while the arrow changes under it,
 * ends in an endless ref update and a white screen (OCT-5524). Whatever changes the
 * arrow, here or from another client, this one just stays.
 */
export const SelectedArrowToolbar: React.FC<Props> = ({ arrow }) => {
  const { sourceColor } = useArrowSource(arrow.relation[0], arrow.colorId)

  const selectedPointId = useSelector<AppState, string | null>(
    ({ canvas }) => canvas.arrow.selectedArrowGridPointId
  )

  // The selection outlives its point when another client or undo removes it, and a
  // smooth arrow draws no points at all: the toolbar then stands at the arrow.
  const pointId =
    arrow.type !== 'smooth' &&
    arrow.points.some(({ id }) => id === selectedPointId)
      ? selectedPointId
      : null

  const pointSelector = `[data-arrow-point-control-id="${pointId}"]`

  return (
    <ArrowActionsToolbar
      anchorSelector={
        pointId ? pointSelector : `[data-arrow-toolbar-anchor="${arrow.id}"]`
      }
      colorId={arrow.colorId}
      id={arrow.id}
      ignoreClickAwaySelector={pointId ? pointSelector : ''}
      pointId={pointId ?? ''}
      sourceColor={sourceColor}
      type={arrow.type}
    />
  )
}
